import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, AlertTriangle, CreditCard, Phone, Mail, Globe,
  ShieldCheck, Sparkles, FileText, Receipt,
} from "lucide-react";

const fmtEur = (cents) => `${((cents || 0) / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

/**
 * QuotePublicPage — Phase 33k (public)
 * /devis-client/{token} : page client pour consulter le devis et payer l'acompte.
 */
export default function QuotePublicPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const paid = searchParams.get("paid"); // "success" | "cancelled"
  const sessionId = searchParams.get("session_id");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [polling, setPolling] = useState(false);
  const [paidStatus, setPaidStatus] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/quotes/public/${token}`);
      setData(r.data);
    } catch {
      setData(false);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line

  // Polling après retour Stripe success
  useEffect(() => {
    if (paid !== "success" || !sessionId || !token) return;
    setPolling(true);
    let attempts = 0;
    const maxAttempts = 7;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const r = await api.get(`/quotes/public/${token}/payment-status?session_id=${sessionId}`);
        if (r.data.payment_status === "paid") {
          clearInterval(interval);
          setPaidStatus(r.data);
          setPolling(false);
          load();
          return;
        }
      } catch {}
      if (attempts >= maxAttempts) {
        clearInterval(interval);
        setPolling(false);
        toast.warning("Paiement en cours de validation…");
        load();
      }
    }, 2200);
    return () => clearInterval(interval);
  }, [paid, sessionId, token]); // eslint-disable-line

  const goPay = async () => {
    setPaying(true);
    try {
      const r = await api.post(`/quotes/public/${token}/checkout`);
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur paiement");
      setPaying(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400"/></div>;

  if (!data || data === false) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="p-8 max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-rose-500 mx-auto mb-3"/>
          <h2 className="text-xl font-bold mb-2">Devis introuvable</h2>
          <p className="text-sm text-slate-600">Ce lien est invalide ou a expiré.</p>
        </Card>
      </div>
    );
  }

  const { quote: q, agency } = data;
  const contact = q.contact || {};
  const isPaid = q.status === "accepted_paid" || q.status === "completed" || paidStatus?.payment_status === "paid";

  return (
    <div className="min-h-screen py-8" style={{ background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)" }} data-testid="quote-public-page">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header co-brandé */}
        <div className="rounded-3xl overflow-hidden shadow-2xl bg-white mb-5" style={{ background: agency.navy }}>
          <div className="p-7 text-white">
            <div className="flex items-center gap-4 mb-4">
              {agency.logo_url && <img src={agency.logo_url} alt={agency.name} className="w-14 h-14 rounded-full object-cover ring-2 ring-white/30"/>}
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] opacity-80 font-bold">Devis personnalisé</div>
                <div className="text-2xl font-black">{agency.name}</div>
              </div>
            </div>
            <div className="bg-white/10 rounded-xl p-4 backdrop-blur">
              <div className="grid sm:grid-cols-3 gap-3 text-xs">
                <div><div className="opacity-70 uppercase tracking-wider font-bold mb-1">Référence</div><div className="font-mono text-base font-bold">{q.number}</div></div>
                <div><div className="opacity-70 uppercase tracking-wider font-bold mb-1">Pour</div><div className="font-bold">{contact.name}{contact.company && ` · ${contact.company}`}</div></div>
                <div><div className="opacity-70 uppercase tracking-wider font-bold mb-1">Validité</div><div className="font-bold">{q.valid_until ? new Date(q.valid_until).toLocaleDateString("fr-FR") : "—"}</div></div>
              </div>
            </div>
          </div>
        </div>

        {/* Statut payé ou refus */}
        {paid === "cancelled" && !isPaid && (
          <Card className="p-4 mb-5 bg-amber-50 border-amber-200 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0"/>
            <div className="text-sm text-amber-900">Paiement annulé. Vous pouvez retenter ci-dessous.</div>
          </Card>
        )}
        {polling && (
          <Card className="p-4 mb-5 bg-sky-50 border-sky-200 flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-sky-600 animate-spin shrink-0"/>
            <div className="text-sm text-sky-900">Validation du paiement en cours…</div>
          </Card>
        )}
        {isPaid && (
          <Card className="p-5 mb-5 bg-emerald-50 border-emerald-300" data-testid="quote-paid-banner">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-7 h-7 text-emerald-600 shrink-0 mt-0.5"/>
              <div>
                <h3 className="font-black text-emerald-900 text-lg">Acompte reçu — Merci {contact.name} !</h3>
                <p className="text-sm text-emerald-800 mt-1">Votre devis est validé. Notre équipe vous contacte sous peu pour planifier la suite. Solde restant à régler à la livraison : <strong>{fmtEur(q.balance_cents)}</strong>.</p>
              </div>
            </div>
          </Card>
        )}

        {/* Lignes */}
        <Card className="overflow-hidden mb-4" data-testid="quote-public-lines">
          <div className="p-5 border-b bg-slate-50">
            <h2 className="font-bold text-slate-900">Détail de la prestation</h2>
            <p className="text-xs text-slate-500 mt-1">{q.template_label}</p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr><th className="text-left p-3 text-[10px] uppercase font-bold text-slate-500">Prestation</th>
                  <th className="text-center p-3 text-[10px] uppercase font-bold text-slate-500">Qté</th>
                  <th className="text-right p-3 text-[10px] uppercase font-bold text-slate-500">PU HT</th>
                  <th className="text-right p-3 text-[10px] uppercase font-bold text-slate-500">Total HT</th></tr>
            </thead>
            <tbody className="divide-y">
              {(q.lines || []).map((l, i) => (
                <tr key={i}>
                  <td className="p-3 font-medium text-slate-900">{l.label}</td>
                  <td className="p-3 text-center text-slate-600 text-xs">{l.qty} {l.unit}</td>
                  <td className="p-3 text-right text-slate-600">{fmtEur(l.unit_price_cents)}</td>
                  <td className="p-3 text-right font-bold">{fmtEur(l.total_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="p-5 bg-slate-50 border-t">
            <table className="w-full text-sm">
              <tr><td className="text-right text-slate-600 py-1">Sous-total HT</td><td className="text-right font-bold py-1 w-32">{fmtEur(q.subtotal_ht_cents)}</td></tr>
              <tr><td className="text-right text-slate-600 py-1">TVA ({q.vat_rate_pct}%)</td><td className="text-right font-bold py-1">{fmtEur(q.vat_cents)}</td></tr>
              <tr className="border-t"><td className="text-right py-2 font-bold text-base">Total TTC</td><td className="text-right py-2 font-black text-xl" style={{ color: agency.navy }}>{fmtEur(q.total_ttc_cents)}</td></tr>
              {q.deposit_amount_cents > 0 && (
                <>
                  <tr className="bg-amber-50"><td className="text-right py-2 font-bold text-amber-800 px-3">Acompte demandé</td><td className="text-right py-2 font-black text-lg text-amber-800 px-3">{fmtEur(q.deposit_amount_cents)}</td></tr>
                  <tr><td className="text-right py-1 text-xs text-slate-500">Solde à la livraison</td><td className="text-right py-1 text-sm font-bold text-slate-700">{fmtEur(q.balance_cents)}</td></tr>
                </>
              )}
            </table>
          </div>
        </Card>

        {q.comment && (
          <Card className="p-4 mb-4 bg-slate-50">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Note de l'équipe</div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{q.comment}</p>
          </Card>
        )}

        {/* CTA paiement */}
        {!isPaid && q.deposit_amount_cents > 0 && q.status === "sent" && (
          <Card className="p-6 text-center bg-gradient-to-br from-sky-50 to-emerald-50 border-2 border-sky-200">
            <ShieldCheck className="w-10 h-10 text-emerald-600 mx-auto mb-3"/>
            <h3 className="text-xl font-black text-slate-900 mb-1">Accepter ce devis</h3>
            <p className="text-sm text-slate-600 mb-5">En cliquant ci-dessous, vous acceptez le devis et réglez l'acompte de <strong>{fmtEur(q.deposit_amount_cents)}</strong> par carte bancaire (paiement sécurisé Stripe).</p>
            <Button onClick={goPay} disabled={paying} className="rounded-full bg-emerald-600 hover:bg-emerald-700 px-8 h-12 text-base font-bold" data-testid="quote-pay-btn">
              {paying ? <Loader2 className="w-5 h-5 mr-2 animate-spin"/> : <CreditCard className="w-5 h-5 mr-2"/>}
              {paying ? "Redirection…" : `Payer l'acompte ${fmtEur(q.deposit_amount_cents)}`}
            </Button>
            <div className="mt-4 text-[10px] text-slate-400 inline-flex items-center gap-1">
              <ShieldCheck className="w-3 h-3"/> Paiement sécurisé · 3D Secure · données chiffrées
            </div>
          </Card>
        )}

        {/* Footer agence */}
        <div className="mt-6 text-center text-xs text-slate-500 space-y-1">
          <div className="font-bold text-slate-700">{agency.name}{agency.siret && <span className="text-slate-400 font-normal"> · SIRET {agency.siret}</span>}</div>
          <div className="flex items-center gap-3 justify-center flex-wrap">
            {agency.phone && <a href={`tel:${agency.phone}`} className="inline-flex items-center gap-1 hover:text-slate-700"><Phone className="w-3 h-3"/>{agency.phone}</a>}
            {agency.email && <a href={`mailto:${agency.email}`} className="inline-flex items-center gap-1 hover:text-slate-700"><Mail className="w-3 h-3"/>{agency.email}</a>}
            {agency.website && <a href={agency.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-slate-700"><Globe className="w-3 h-3"/>{agency.website.replace(/^https?:\/\//, "")}</a>}
          </div>
        </div>
      </div>
    </div>
  );
}
